import { useState, useEffect } from "react";
import { AnimTiltedCard } from "@readdy/anim/card/tilted-card/react";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function Features() {
  const [reducedMotion, setReducedMotion] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const CardWrapper = reducedMotion || isMobile
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : ({ children }: { children: React.ReactNode }) => (
        <AnimTiltedCard maxTilt={12} strength={0.08}>
          {children}
        </AnimTiltedCard>
      );

  return (
    <section id="feature" className="bg-background-50 py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl leading-tight text-foreground-950">
              選ばれる理由は、<br />
              シンプルです。
            </h2>
          </div>
          <div className="md:pt-4">
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              駐車場は、"どこに停めるか"ではなく、
              "どう試合に向かい、どう帰るか"で選ぶ時代に。
              アネットパーキングは、観戦体験そのものを設計しました。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-14 md:mt-20">
          <CardWrapper>
            <div className="md:col-span-1 bg-background-100 rounded-3xl overflow-hidden p-5">
              <div className="relative h-56 md:h-64 rounded-2xl overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/1655f95b-cd6b-479d-8503-b76e04453858/1008c837-c5dd-4a54-8db1-283cea7bf081_compressed_processed-1142D937-0A09-4FBF-9CE4-5EC9EEAEE6F3.webp"
                  alt="快適な車内"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-5 px-2">
                <div className="font-heading text-xl text-foreground-950">
                  冷暖房完備のシャトル
                </div>
                <div className="text-foreground-600 text-sm mt-2 leading-relaxed">
                  真夏の炎天下も、冬の雪道も、車内はいつも快適。
                  お子様連れやご高齢のご家族にも安心です。車椅子やベビーカーなどもシャトルにお乗せ致します。
                </div>
              </div>
            </div>
          </CardWrapper>

          <CardWrapper>
            <div className="bg-background-100 rounded-3xl overflow-hidden p-5 flex flex-col">
              <div className="text-center pt-4 pb-3">
                <div className="font-heading text-xl text-foreground-950">
                  LINEでかんたん予約
                </div>
                <div className="text-foreground-600 text-sm mt-2 leading-relaxed px-3">
                  公式LINE「アネットパーキング」から
                  トークで空き確認＆即予約。当日もOK。
                  【LINE登でお得なサービスを実施中！！】
                </div>
                <a
                  href="https://lin.ee/v0fi5uo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-external-link-line mr-1 align-middle" />
                  公式LINEはこちら
                </a>
              </div>
              <div className="relative h-56 md:h-64 rounded-2xl overflow-hidden mt-3">
                <img
                  src="https://storage.readdy-site.link/project_files/1655f95b-cd6b-479d-8503-b76e04453858/937af86d-e811-4778-8e92-5bb02922e351_compressed_processed-22A57A4F-8770-44FE-AFDA-BDD35C6E045A.webp"
                  alt="24時間予約"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </CardWrapper>

          <CardWrapper>
            <div className="bg-background-100 rounded-3xl overflow-hidden p-5 flex flex-col">
              <div className="text-center pt-4 pb-3">
                <div className="font-heading text-xl text-foreground-950">
                  試合後もすぐ運行
                </div>
                <div className="text-foreground-600 text-sm mt-2 leading-relaxed px-3">
                  試合終了と同時にシャトルが待機。
                  大渋滞を避けてスムーズに帰路へ。
                </div>
              </div>
              <div className="relative h-56 md:h-64 rounded-2xl overflow-hidden mt-3">
                <img
                  src="https://storage.readdy-site.link/project_files/1655f95b-cd6b-479d-8503-b76e04453858/9815a7d5-5ac9-4a83-8a9e-147d3561fb3e_compressed_processed-6CB6FE26-AE5E-4C30-93C8-0AFFFBDDFE6E.webp"
                  alt="試合後もすぐ運行"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </CardWrapper>
        </div>
      </div>
    </section>
  );
}